import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Place } from '@/types';

export interface HousingStockRecord {
  Place: string;
  place_fips: string;
  year: string | number;
  pct_sf_existing: number;
  pct_mf_existing: number;
}

interface PeerHousingStockData {
  [key: string]: HousingStockRecord[]; // key is place_fips
}

export function usePeerHousingStockData(peers: Place[]) {
  const [data, setData] = useState<PeerHousingStockData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!peers.length) {
      setLoading(false);
      setData({});
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Create an array of promises for each peer
    const fetchPromises = peers.map(peer => 
      supabase
        .from('acs_bps_metrics_housing_stock_analysis')
        .select('*')
        .eq('Place', peer.name)
        .eq('place_fips', peer.place_fips)
        .order('year', { ascending: false })
        .limit(1) // We only need the most recent year for ranking
    );

    Promise.all(fetchPromises)
      .then(results => {
        const newData: PeerHousingStockData = {};
        let loggedAny = false;
        results.forEach((result, index) => {
          if (result.error) {
            console.error(`Error fetching data for peer ${peers[index].name}:`, result.error);
          } else {
            const peerData = result.data || [];
            newData[peers[index].place_fips] = peerData;
            // Log the housing stock data for this peer
            const latestData = peerData[0];
            if (latestData) {
              loggedAny = true;
              console.log(`Peer City: ${peers[index].name}`);
              console.log(`  FIPS: ${peers[index].place_fips}`);
              console.log(`  MF Share: ${latestData.pct_mf_existing?.toFixed(2)}%`);
              console.log(`  SF Share: ${latestData.pct_sf_existing?.toFixed(2)}%`);
              console.log(`  Year: ${latestData.year}`);
            }
          }
        });
        if (loggedAny) {
          console.log('------------------------');
        }
        setData(newData);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching peer housing stock data:', err);
        setError('Failed to fetch peer data');
        setLoading(false);
      });
  }, [peers]);

  return { data, loading, error };
} 